import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';

const Studenttype = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ type: '' });
  const [loading, setLoading] = useState(false);
  const [studentTypes, setStudentTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  // Fetch schoolId from users collection
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        setSchoolId(userDoc.data().schoolId);
      } else {
        toast.error("User data not found");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      toast.error("Error fetching user data");
    }
  };

  // Fetch student types for the school
  const fetchStudentTypes = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(
        collection(db, 'studenttypes'),
        where('schoolId', '==', schoolId)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudentTypes(data);
    } catch (error) {
      console.error("Error fetching student types:", error);
    }
  };

  useEffect(() => {
    fetchSchoolId();
  }, []);

  useEffect(() => {
    if (schoolId) fetchStudentTypes(schoolId);
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ type: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { type } = formData;

    if (!type.trim()) {
      toast.error("Type is required");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    try {
      const data = { type, schoolId };

      if (editMode && editId) {
        await updateDoc(doc(db, 'studenttypes', editId), data);
        toast.success("Student type updated");
      } else {
        await addDoc(collection(db, 'studenttypes'), data);
        toast.success("Student type added");
      }

      fetchStudentTypes(schoolId);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (item) => {
    setFormData({ type: item.type });
    setEditId(item.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'studenttypes', id));
      toast.success("Student type deleted");
      fetchStudentTypes(schoolId);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting student type");
    }
  };

  const paginated = studentTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Student Type</Button>
        <Typography variant="h5" fontWeight="bold">Student Types</Typography>
      </Stack>

      <Card>
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">No student types found</TableCell>
                </TableRow>
              ) : (
                paginated.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{item.type}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={studentTypes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Student Type</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Studenttype;